import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useComposerDraft } from '@multiplayer-app/ai-agent-react'
import { useShell } from '@/shared/context/ShellContext'
import { useChecklistScan } from './useChecklistScan'
import { HistoryView } from './components/HistoryView'
import { ScanDrawer } from './components/ScanDrawer'
import { ScanActionsProvider, type ScanActions } from './ScanActionsContext'
import { buildScanResultAttachment, buildScanRunAttachment, type NamedScanResult } from './scanResultAttachment'
import type { ScanResult } from './types'

/**
 * The full checklist-scan experience: the open document's scan history, with a
 * Scan button that opens the checklist-item picker drawer (pick -> scan ->
 * review/mark). New runs surface in the history and resolve in place.
 *
 * The pane stays mounted across chat/scan toggles (App hides it rather than
 * unmounting), so its state and scroll persist. When it becomes visible again
 * we silently refresh the lists so they reflect changes made elsewhere.
 */
export function ChecklistScanPane() {
  const scan = useChecklistScan()
  const { showScan, toggleView } = useShell()
  const { addAttachments, draft, setValue } = useComposerDraft()
  const wasShown = useRef(showScan)

  useEffect(() => {
    if (showScan && !wasShown.current) scan.refresh()
    wasShown.current = showScan
  }, [showScan, scan.refresh])

  const sendToChat = useCallback(
    (result: ScanResult, name: string) => {
      addAttachments([buildScanResultAttachment(result, name, scan.documentName)])
      if (!draft.value.trim()) {
        setValue('Analyze this scan result and propose a safe document fix with an Apply card.')
      }
      toggleView()
    },
    [addAttachments, draft.value, setValue, toggleView, scan.documentName]
  )

  const sendRunToChat = useCallback(
    (items: NamedScanResult[]) => {
      if (!items.length) return
      addAttachments([buildScanRunAttachment(items, scan.documentName)])
      if (!draft.value.trim()) {
        setValue(
          'Analyze these scan results and propose safe document fixes. ' +
            'Propose every actionable fix together in one batch I can apply as tracked changes.'
        )
      }
      toggleView()
    },
    [addAttachments, draft.value, setValue, toggleView, scan.documentName]
  )

  const actions = useMemo<ScanActions>(() => ({ sendToChat, sendRunToChat }), [sendToChat, sendRunToChat])

  return (
    <ScanActionsProvider value={actions}>
      <div className='ramp-scan'>
        <HistoryView scan={scan} />
        {scan.drawerOpen ? <ScanDrawer scan={scan} /> : null}
      </div>
    </ScanActionsProvider>
  )
}
